import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, RefreshCcw, Save, Info } from "lucide-react";

/**
 * Admin · Integrations · Market Approval
 *
 * Country-specific approval matrix for each VAULT Connect provider. No
 * connector is activated by simply flipping a row here — the master
 * `vault_connect` feature flag remains OFF until every reviewed row for
 * a region reaches Approved / Beta / Live.
 */

const STATUSES = [
    "Research", "Provider Review", "Security Review",
    "Privacy Review", "Legal Review", "Approved",
    "Beta", "Live", "Blocked",
];

const STATUS_TONE = {
    Research:          "bg-stone-100 text-stone-800",
    "Provider Review": "bg-blue-50 text-blue-800",
    "Security Review": "bg-blue-50 text-blue-800",
    "Privacy Review":  "bg-blue-50 text-blue-800",
    "Legal Review":    "bg-blue-50 text-blue-800",
    Approved:          "bg-emerald-50 text-emerald-800",
    Beta:              "bg-amber-50 text-amber-900",
    Live:              "bg-emerald-100 text-emerald-900",
    Blocked:           "bg-rose-50 text-rose-800",
};

export default function MarketApproval() {
    const [rows, setRows] = useState([]);
    const [providers, setProviders] = useState([]);
    const [form, setForm] = useState({ provider_id: "", country: "", status: "Research", notes: "" });

    const load = async () => {
        try {
            const [r, p] = await Promise.all([
                api.get("/vault-connect/market-approvals"),
                api.get("/vault-connect/providers"),
            ]);
            setRows(r.data?.rows || []); setProviders(p.data?.providers || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load market approvals"); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.provider_id || !form.country || !form.status) {
            toast.error("Provider, country and status are required"); return;
        }
        try {
            await api.post("/vault-connect/market-approvals", form);
            toast.success("Saved");
            setForm({ provider_id: "", country: "", status: "Research", notes: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="market-approval-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <ShieldCheck size={12} /> Admin · Integrations
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Market approval.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Track regional approvals for each connector. VAULT Connect stays disabled
                        until reviews are complete — this table is your source of truth for rollout.
                    </p>
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                        data-testid="market-approval-refresh">
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {/* Upsert form */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3"
                 data-testid="market-approval-form">
                <label className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Provider
                    <select value={form.provider_id} onChange={e => setForm({...form, provider_id: e.target.value})}
                            className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                            data-testid="market-approval-provider">
                        <option value="">Select…</option>
                        {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                <label className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Country (ISO)
                    <input value={form.country} onChange={e => setForm({...form, country: e.target.value.toUpperCase()})}
                           maxLength={3}
                           className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm uppercase"
                           data-testid="market-approval-country" />
                </label>
                <label className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Status
                    <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                            className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                            data-testid="market-approval-status">
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold md:col-span-1">Notes
                    <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                           className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                           data-testid="market-approval-notes" />
                </label>
                <button onClick={save}
                        className="mt-auto px-4 py-2 rounded-full bg-amber-500 text-stone-900 text-[13px] font-bold hover:bg-amber-400 inline-flex items-center gap-1 justify-center"
                        data-testid="market-approval-save">
                    <Save size={12} /> Save
                </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
                 data-testid="market-approval-table">
                <table className="w-full text-sm"><thead className="bg-stone-50 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold"><tr><th className="text-left px-4 py-2.5">Provider</th><th className="text-left px-4 py-2.5">Country</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Notes</th><th className="text-left px-4 py-2.5">Updated</th></tr></thead><tbody>{rows.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-sm text-stone-400 py-8">
                                No approval rows yet. Add the first one above.
                            </td></tr>
                        ) : rows.map(r => (
                            <tr key={r.id || `${r.provider_id}-${r.country}`}
                                className="border-t border-stone-100"
                                data-testid={`market-approval-row-${r.provider_id}-${r.country}`}>
                                <td className="px-4 py-2.5 text-[13px] font-semibold text-stone-900">{r.provider_id}</td>
                                <td className="px-4 py-2.5 text-[13px] tabular-nums">{r.country}</td>
                                <td className="px-4 py-2.5">
                                    <span className={`inline-block text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${STATUS_TONE[r.status] || "bg-stone-100 text-stone-700"}`}>
                                        {r.status}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-[12px] text-stone-600">{r.notes || "—"}</td>
                                <td className="px-4 py-2.5 text-[11.5px] text-stone-500">{r.updated_at?.slice(0, 10) || "—"}<br/><span className="text-[10px]">by {r.updated_by || "—"}</span></td>
                            </tr>
                        ))}</tbody></table>
            </div>

            <p className="mt-6 text-[11.5px] text-stone-500 max-w-3xl flex items-start gap-2">
                <Info size={12} className="mt-0.5 shrink-0" />
                <span>
                    Approvals recorded here do <b>not</b> activate any connector. The master{" "}
                    <code className="font-mono text-[11px] px-1 py-0.5 bg-stone-100 rounded">vault_connect</code>{" "}
                    feature flag must be toggled ON separately by a platform administrator once every
                    required region-level review reaches Approved / Beta / Live.
                </span>
            </p>

            <RolloutWizard rows={rows} providers={providers} />
        </div>
    );
}

/* ─────────────── Country Approval Rollout Wizard ─────────────── */
const STAGE_ORDER = [
    "Research", "Provider Review", "Security Review",
    "Privacy Review", "Legal Review", "Approved", "Beta", "Live",
];
function RolloutWizard({ rows, providers }) {
    // Group approvals by country and show the furthest stage reached
    // for each provider row. A country is "unlocked" only when every
    // approved provider has reached Approved/Beta/Live.
    const byCountry = {};
    rows.forEach(r => {
        (byCountry[r.country] = byCountry[r.country] || []).push(r);
    });
    const countries = Object.keys(byCountry).sort();
    if (countries.length === 0) return null;
    return (
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl overflow-hidden"
             data-testid="rollout-wizard">
            <div className="px-4 py-3 border-b border-stone-100">
                <h2 className="text-lg font-display text-stone-900">Country rollout wizard</h2>
                <p className="text-[11.5px] text-stone-500 mt-0.5">
                    Guided view of each region&rsquo;s progress from Research → Live.
                </p>
            </div>
            {countries.map(country => {
                const items = byCountry[country];
                const allLive = items.every(r => ["Approved", "Beta", "Live"].includes(r.status));
                return (
                    <div key={country} className="border-b border-stone-100 last:border-0 px-4 py-3"
                         data-testid={`rollout-country-${country}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                            <div className="text-[13.5px] font-semibold text-stone-900">{country}</div>
                            <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${allLive ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>
                                {allLive ? "READY TO ENABLE FLAG" : "IN REVIEW"}
                            </span>
                        </div>
                        <div className="space-y-1.5">
                            {items.map(r => {
                                const idx = STAGE_ORDER.indexOf(r.status);
                                return (
                                    <div key={`${r.provider_id}-${r.country}`}
                                         className="flex items-center gap-1.5"
                                         data-testid={`rollout-stage-${r.provider_id}-${r.country}`}>
                                        <div className="w-32 text-[11.5px] text-stone-700 truncate">{r.provider_id}</div>
                                        <div className="flex gap-1 flex-wrap">
                                            {STAGE_ORDER.map((s, i) => (
                                                <span key={s}
                                                      className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full border ${i <= idx ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 text-stone-400"}`}>
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
